// Copyright (c) 2026 Optimum Healthcare IT. All rights reserved.
// Author: Frank Scalzo <Optimum Healthcare IT>
// License: proprietary
// provenance-id: 70b34e38-d715-4a94-ace5-e7f081cb7fdc

// ---------------------------------------------------------------------------
// Primordia — Survey Workflow
// ---------------------------------------------------------------------------
// Discovery's real bottleneck is not the CMDB, it is chasing application owners
// by email. This turns that chase into the product loop:
//
//   1. Campaigns  — scope a questionnaire to a set of apps + a few fact-sheet
//                   SECTIONS, pick recipients (derived from the owner fields on
//                   the apps themselves), send, then chase what is outstanding.
//   2. Respond    — the owner's view. Only the scoped fields, friendly labels,
//                   helper text, a progress bar, save-draft and submit. On
//                   submit the answers merge into the fact sheet, so completion
//                   rises with every answer.
//   3. Outstanding— the chase list: which apps still lack which MANDATORY
//                   fields, grouped by the person who owns them.
//
// Delivery (email) is a server concern: we call /surveys, /surveys/send and
// /surveys/responses when the API is there. When it is not, campaigns are kept
// in localStorage ('primordia.surveys') and the UI says so plainly. Tabs 2 and 3
// are fully offline against the local store — they are the parts that create
// value today.
//
// None of those three endpoints is deployed today, so NOTHING is emailed to an
// application owner. Every label on this screen has to make that explicit —
// a consultant who believes an owner was contacted will not chase them, and the
// discovery stalls with nobody noticing.
// ---------------------------------------------------------------------------

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Button, Tabs, Tab, Table, TableHead, TableRow,
  TableCell, TableBody, TableSortLabel, Chip, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Alert, Checkbox, FormControlLabel,
  CircularProgress, Tooltip, IconButton, LinearProgress, Divider, Autocomplete,
  Accordion, AccordionSummary, AccordionDetails, ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import SaveIcon from '@mui/icons-material/Save';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { getConfig } from './config';
import { getIdToken } from './auth';
import { store, completionOver } from './store';
import { allFieldsFor, resolvePicklist, FIELD_TYPES } from './schema';

// ---- Optimum palette -------------------------------------------------------
const NAVY = '#021F4B';
const BLUE = '#0D5B9A';
const GREY = '#5C6B7A';
const HEAD = 'sectionHeader.main';
const headSx = { '& th': { fontWeight: 700, color: HEAD } };

const LS_SURVEYS = 'primordia.surveys';
const LS_DRAFTS = 'primordia.surveys.drafts';

// ---- Schema-derived constants (the schema is static, so compute once) -------
const APP_FIELDS = allFieldsFor('Application').filter(
  (f) => f.type !== FIELD_TYPES.LIFECYCLE && f.type !== FIELD_TYPES.RELATION
);
const SECTION_NAMES = APP_FIELDS.reduce((acc, f) => (acc.includes(f.section) ? acc : acc.concat(f.section)), []);
const SECTION_SIZE = SECTION_NAMES.reduce((acc, s) => {
  acc[s] = APP_FIELDS.filter((f) => f.section === s).length;
  return acc;
}, {});
// One row per field key (hostingType / vendorName appear in two sections).
const UNIQUE_FIELDS = APP_FIELDS.filter((f, i) => APP_FIELDS.findIndex((x) => x.key === f.key) === i);
const MANDATORY_FIELDS = UNIQUE_FIELDS.filter((f) => f.mandatory);

const OWNER_FIELDS = [
  { key: 'applicationOwner', role: 'Business Owner' },
  { key: 'technicalOwner', role: 'Technical Owner' },
  { key: 'primaryStakeholder', role: 'Primary Stakeholder' },
];

// ---- Small helpers ---------------------------------------------------------
const isEmpty = (v) => v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length);
const isEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());
const uid = (p) => `${p}-${Date.now().toString(36)}-${Math.floor(Math.random() * 1e4).toString(36)}`;
const shortDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : '—');
const pct = (n, d) => (d ? Math.round((n / d) * 100) : 0);
// Rendered options per picker. Selection and enrichment always cover the full
// set; this only bounds what is mounted.
const PICKER_CAP = 250;

const readLocal = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (e) { return fallback; }
};
const writeLocal = (key, value) => {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* quota */ }
};

// Fields in the selected sections, de-duplicated by key.
// `findIndex` inside `filter` made this O(F^2) on every call, and it is called
// from the render path and from `statsFor` for every campaign row.
const fieldsForSections = (sections) => {
  const wanted = new Set(sections || []);
  const seen = new Set();
  return APP_FIELDS.filter((f) => {
    if (!wanted.has(f.section) || seen.has(f.key)) return false;
    seen.add(f.key);
    return true;
  });
};

// Plain-English helper text. Specific where it matters, generic otherwise —
// this is what makes the owner's form feel like a form and not a database.
const HELP = {
  description: 'A sentence or two a non-technical colleague would understand.',
  appPurpose: 'What does this application actually do for your team day to day?',
  applicationOwner: 'The business person accountable for this application.',
  technicalOwner: 'Who supports it technically — your team or a vendor?',
  primaryStakeholder: 'The person who feels it first when this system is down.',
  primaryStakeholderDirector: 'Their director — who we escalate to.',
  vendorName: 'The company that supplies the software.',
  vendorContact: 'Name of your day-to-day contact at the vendor.',
  vendorEmail: 'Vendor email address, so we can ask them the technical questions.',
  hostingType: 'Where it runs today: your data centre, a vendor cloud (SaaS), or cloud infrastructure.',
  hostingDescription: 'Anything unusual about how or where it is hosted.',
  rto: 'How long could you work without this system before it becomes a real problem?',
  rpo: 'How much recent data could you afford to re-enter after an outage?',
  phi: 'Does it hold patient data (PHI)?',
  patientSafetyImpact: 'If this is down, what is the risk to patient care?',
  downtimeProcedures: 'Do you have a documented paper or manual process for downtime?',
  remoteWork: 'Can your team use this from outside the hospital network?',
  ssoAvailability: 'Do users sign in with their normal network account?',
  latencySensitive: 'Does it feel slow or break if the network is slower?',
  hardcodedIps: 'Are there fixed IP addresses configured anywhere (devices, interfaces, scripts)?',
  normalHours: 'When is it normally used — e.g. Mon-Fri 7am-7pm, or 24/7.',
  peakPeriods: 'Busiest times: month-end, shift change, flu season…',
  mustWhitelist: 'Any addresses, URLs or ports that must be allowed through the firewall.',
  serverInventory: 'Server names if you know them — a rough list is fine.',
  lifecycleState: 'Is it being planned, live today, or on the way out?',
};
const GENERIC = {
  [FIELD_TYPES.TEXTAREA]: 'As much detail as you have — bullet points are fine.',
  [FIELD_TYPES.SELECT]: 'Pick the closest option.',
  [FIELD_TYPES.MULTISELECT]: 'Choose all that apply.',
  [FIELD_TYPES.BOOLEAN]: 'Yes or no — leave blank if you are not sure.',
  [FIELD_TYPES.NUMBER]: 'A number.',
  [FIELD_TYPES.CURRENCY]: 'Approximate annual cost in dollars.',
  [FIELD_TYPES.PERCENT]: 'A percentage from 0 to 100.',
  [FIELD_TYPES.DATE]: 'Pick a date.',
  [FIELD_TYPES.URL]: 'Full web address, starting with https://',
};
const helperFor = (f) => HELP[f.key] || GENERIC[f.type] || 'Short answer.';

const DEFAULT_MESSAGE =
  'We are documenting our application estate ahead of the cloud programme. ' +
  'You are listed as an owner for the applications below. The questions are short and ' +
  'scoped to your area — if you do not know an answer, leave it blank and we will chase it elsewhere. ' +
  'Thank you.';

// ---- Presentational bits ---------------------------------------------------
function Progress({ value, width = 130, showLabel = true }) {
  const v = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <LinearProgress
        variant="determinate"
        value={v}
        sx={{
          width, height: 8, borderRadius: 4, bgcolor: '#E6ECF3',
          '& .MuiLinearProgress-bar': { bgcolor: v >= 100 ? '#2E7D32' : BLUE, borderRadius: 4 },
        }}
      />
      {showLabel && <Typography variant="caption" sx={{ color: GREY, minWidth: 34 }}>{v}%</Typography>}
    </Stack>
  );
}

function SectionChips({ sections, max = 2 }) {
  const shown = sections.slice(0, max);
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {shown.map((s) => <Chip key={s} size="small" label={s} variant="outlined" sx={{ height: 20 }} />)}
      {sections.length > max && (
        <Chip size="small" label={`+${sections.length - max}`} sx={{ height: 20, bgcolor: '#E6ECF3' }} />
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function SurveyWorkflow({ tenantId }) {
  const [tab, setTab] = useState(0);
  const [apps, setApps] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [localMode, setLocalMode] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState(false);

  // campaign dialog
  const [dialog, setDialog] = useState(null); // draft campaign or null
  const [emailEdits, setEmailEdits] = useState({});
  const [missingFieldKey, setMissingFieldKey] = useState('');

  // respond tab
  const [respCampaignId, setRespCampaignId] = useState('');
  const [respAppId, setRespAppId] = useState('');
  const [respSections, setRespSections] = useState([]);
  const [respondent, setRespondent] = useState('');
  const [answers, setAnswers] = useState({});
  const [baseline, setBaseline] = useState({});

  // outstanding tab
  const [sortBy, setSortBy] = useState('missing');
  const [sortDir, setSortDir] = useState('desc');

  const api = useCallback(async (path, options = {}) => {
    const res = await fetch(`${getConfig().apiUrl}${path}`, {
      ...options,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${getIdToken()}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    // An error envelope is often an HTML page, not JSON. Parsing it bare threw
    // SyntaxError here — before the !res.ok check below could report the real
    // status — so a 502 surfaced as "Unexpected token <". Same guarded form as
    // AiEnrichment and RelationshipWizard.
    let data = {};
    try { data = text ? JSON.parse(text) : {}; } catch (e) { data = {}; }
    if (!res.ok) throw new Error(data.error || data.message || `request failed (${res.status})`);
    return data;
  }, []);

  const localCampaigns = useCallback(
    () => (readLocal(LS_SURVEYS, []) || []).filter((c) => !tenantId || c.tenantId === tenantId),
    [tenantId]
  );

  const saveLocalCampaigns = useCallback((next) => {
    const others = (readLocal(LS_SURVEYS, []) || []).filter((c) => (tenantId ? c.tenantId !== tenantId : false));
    writeLocal(LS_SURVEYS, [...others, ...next]);
  }, [tenantId]);

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      setApps(await store.listFactSheets(tenantId, { type: 'Application' }));
    } catch (e) {
      setErr(e.message);
    }
    const q = `?tenantId=${encodeURIComponent(tenantId || '')}`;
    try {
      const data = await api(`/surveys${q}`);
      const rows = Array.isArray(data) ? data : (data.campaigns || []);
      let responses = [];
      try {
        const rd = await api(`/surveys/responses${q}`);
        responses = Array.isArray(rd) ? rd : (rd.responses || []);
      } catch (e) { /* responses endpoint optional */ }
      setCampaigns(rows.map((c) => ({
        ...c,
        sections: c.sections || [],
        appIds: c.appIds || [],
        recipients: c.recipients || [],
        responses: c.responses || responses.filter((r) => r.campaignId === c.id),
      })));
      setLocalMode(false);
    } catch (e) {
      setLocalMode(true);
      setCampaigns(localCampaigns());
    }
    setLoading(false);
  }, [api, tenantId, localCampaigns]);

  useEffect(() => { load(); }, [load]);

  const appById = useMemo(() => {
    const m = {};
    apps.forEach((a) => { m[a.id] = a; });
    return m;
  }, [apps]);

  // ---- Recipient derivation ------------------------------------------------
  // Owners live on the fact sheets themselves. Some tenants store an email,
  // most store a person's name — we surface both and let the user fill the gap.
  const suggestedRecipients = useMemo(() => {
    const map = new Map();
    (dialog ? dialog.appIds : []).forEach((id) => {
      const app = appById[id];
      if (!app) return;
      OWNER_FIELDS.forEach(({ key, role }) => {
        const raw = app.fields ? app.fields[key] : '';
        const value = String(raw || '').trim();
        if (!value) return;
        const k = value.toLowerCase();
        const existing = map.get(k) || {
          key: k,
          name: isEmail(value) ? value.split('@')[0] : value,
          email: isEmail(value) ? value : '',
          roles: [],
          appIds: [],
        };
        if (!existing.roles.includes(role)) existing.roles.push(role);
        if (!existing.appIds.includes(id)) existing.appIds.push(id);
        map.set(k, existing);
      });
    });
    return Array.from(map.values()).sort((a, b) => b.appIds.length - a.appIds.length);
  }, [dialog, appById]);

  // ---- Campaign statistics -------------------------------------------------
  const statsFor = useCallback((c) => {
    const recipients = c.recipients || [];
    const responded = new Set((c.responses || []).map((r) => String(r.email || r.name || '').toLowerCase()));
    const received = recipients.filter((r) => responded.has(String(r.email || r.name || '').toLowerCase())).length;
    const outstanding = Math.max(0, recipients.length - received);
    const fields = fieldsForSections(c.sections || []);
    // Scored with `completionOver`, the same rule the fact-sheet badge uses.
    // This counted a field answered "No" as captured (its `isEmpty` accepts
    // `false`) and skipped the PERCENT 50/50 blend, so the survey read "82% of
    // data captured" beside a fact sheet reading 61% complete, on the same data.
    // The number is still narrower than the badge — it covers only the sections
    // this campaign asks about — which is why the label has to say so.
    const scored = (c.appIds || [])
      .map((id) => appById[id])
      .filter(Boolean)
      .map((app) => completionOver(fields, app.fields));
    const dataPct = scored.length
      ? Math.round(scored.reduce((a, b) => a + b, 0) / scored.length)
      : 0;
    return {
      recipients: recipients.length,
      received,
      outstanding,
      responsePct: pct(received, recipients.length),
      dataPct,
      questions: fields.length,
    };
  }, [appById]);

  // ---- Create / send -------------------------------------------------------
  const openDialog = () => {
    setEmailEdits({}); setMissingFieldKey('');
    setDialog({
      id: uid('cmp'),
      tenantId: tenantId || '',
      name: '',
      appIds: [],
      sections: [],
      recipients: [],
      dueDate: '',
      message: DEFAULT_MESSAGE,
    });
  };

  const setDraft = (patch) => setDialog((d) => ({ ...d, ...patch }));

  const toggleRecipient = (s) => {
    const email = emailEdits[s.key] !== undefined ? emailEdits[s.key] : s.email;
    setDialog((d) => {
      const on = (d.recipients || []).some((r) => r.key === s.key);
      const recipients = on
        ? d.recipients.filter((r) => r.key !== s.key)
        : d.recipients.concat([{ key: s.key, name: s.name, email, roles: s.roles, appIds: s.appIds }]);
      return { ...d, recipients };
    });
  };

  const setRecipientEmail = (key, email) => {
    setEmailEdits((m) => ({ ...m, [key]: email }));
    setDialog((d) => ({ ...d, recipients: (d.recipients || []).map((r) => (r.key === key ? { ...r, email } : r)) }));
  };

  const addManualRecipient = (raw) => {
    const email = String(raw || '').trim();
    if (!email) return;
    setDialog((d) => {
      if ((d.recipients || []).some((r) => r.key === email.toLowerCase())) return d;
      return {
        ...d,
        recipients: d.recipients.concat([{
          key: email.toLowerCase(), name: email.split('@')[0], email, roles: ['Added manually'], appIds: d.appIds,
        }]),
      };
    });
  };

  const addAppsMissingField = () => {
    if (!missingFieldKey) return;
    const ids = apps.filter((a) => isEmpty(a.fields ? a.fields[missingFieldKey] : undefined)).map((a) => a.id);
    setDialog((d) => ({ ...d, appIds: Array.from(new Set(d.appIds.concat(ids))) }));
  };

  const createCampaign = async () => {
    const draft = dialog;
    const campaign = {
      ...draft,
      name: draft.name || 'Untitled campaign',
      createdAt: new Date().toISOString(),
      sentAt: new Date().toISOString(),
      status: 'sent',
      reminders: 0,
      responses: [],
    };
    setBusy(true); setErr(''); setMsg('');
    try {
      await api('/surveys', { method: 'POST', body: JSON.stringify(campaign) });
      await api('/surveys/send', {
        method: 'POST',
        body: JSON.stringify({ campaignId: campaign.id, tenantId: campaign.tenantId, emails: campaign.recipients.map((r) => r.email).filter(Boolean) }),
      }).catch(() => {});
      setMsg(`Campaign "${campaign.name}" sent to ${campaign.recipients.length} recipient(s).`);
      setDialog(null);
      await load();
    } catch (e) {
      const next = campaigns.concat([campaign]);
      setCampaigns(next);
      saveLocalCampaigns(next);
      setLocalMode(true);
      setMsg(`Campaign "${campaign.name}" saved in this browser — ${campaign.recipients.length} recipient(s), ${campaign.appIds.length} application(s). No invitation was emailed: contact the recipients yourself, then record their answers on the Respond tab.`);
      setDialog(null);
    } finally {
      setBusy(false);
    }
  };

  const sendReminders = async (c) => {
    const responded = new Set((c.responses || []).map((r) => String(r.email || r.name || '').toLowerCase()));
    const chase = (c.recipients || []).filter((r) => !responded.has(String(r.email || r.name || '').toLowerCase()));
    if (!chase.length) { setMsg(`Everyone on "${c.name}" has already responded.`); return; }
    setErr(''); setMsg('');
    try {
      await api('/surveys/send', {
        method: 'POST',
        body: JSON.stringify({ campaignId: c.id, tenantId: c.tenantId, reminder: true, emails: chase.map((r) => r.email).filter(Boolean) }),
      });
      setMsg(`Reminders sent to ${chase.length} outstanding recipient(s).`);
      await load();
    } catch (e) {
      const next = campaigns.map((x) => (x.id === c.id
        ? { ...x, reminders: (x.reminders || 0) + 1, lastReminderAt: new Date().toISOString() }
        : x));
      setCampaigns(next);
      saveLocalCampaigns(next);
      setLocalMode(true);
      setMsg(`No reminder was emailed. ${chase.length} recipient(s) still outstanding on "${c.name}" — chase them yourself: ${chase.map((r) => r.email || r.name).join(', ')}`);
    }
  };

  const remindOwner = async (owner) => {
    setErr(''); setMsg('');
    const payload = { tenantId: tenantId || '', reminder: true, emails: [owner.email].filter(Boolean), name: owner.name, appIds: owner.apps.map((a) => a.id) };
    try {
      await api('/surveys/send', { method: 'POST', body: JSON.stringify(payload) });
      setMsg(`Reminder sent to ${owner.email || owner.name}.`);
    } catch (e) {
      setLocalMode(true);
      setMsg(`No reminder was emailed. ${owner.name}${owner.email ? ` (${owner.email})` : ' — no email on file'} has ${owner.missingCount} answer(s) outstanding across ${owner.apps.length} application(s); chase them yourself.`);
    }
  };

  // ---- Respond tab ---------------------------------------------------------
  const respondCampaign = campaigns.find((c) => c.id === respCampaignId) || null;
  const respondApp = appById[respAppId] || null;
  const respondFields = useMemo(() => fieldsForSections(respSections), [respSections]);

  // Pick up a campaign's scope, and prefill from what we already know.
  useEffect(() => {
    if (!respondCampaign) return;
    setRespSections(respondCampaign.sections || []);
    if (!respondCampaign.appIds.includes(respAppId)) setRespAppId(respondCampaign.appIds[0] || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respCampaignId]);

  useEffect(() => {
    const app = appById[respAppId];
    if (!app) { setAnswers({}); return; }
    const drafts = readLocal(LS_DRAFTS, {}) || {};
    const saved = (drafts[respAppId] || {}).answers || {};
    // Remember what was already on file. The form is prefilled from the fact
    // sheet, so without a baseline every prefilled value counted as an answer
    // the respondent had just given — "Thank you, 14 answer(s) saved" after
    // someone typed one. A saved draft IS an answer, so the baseline is the
    // fact sheet alone.
    setBaseline({ ...(app.fields || {}) });
    setAnswers({ ...(app.fields || {}), ...saved });
    const owner = OWNER_FIELDS.map((o) => (app.fields || {})[o.key]).find(Boolean);
    setRespondent((prev) => prev || String(owner || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [respAppId, apps]);

  const sameAsFile = (a, b) => JSON.stringify(a === undefined ? null : a) === JSON.stringify(b === undefined ? null : b);
  // Split the two things the screen was conflating: how much of the form is
  // populated, and how much of it this respondent actually supplied.
  const populatedFields = respondFields.filter((f) => !isEmpty(answers[f.key]));
  const newFields = populatedFields.filter((f) => !sameAsFile(answers[f.key], baseline[f.key]));
  const answeredCount = populatedFields.length;
  const newCount = newFields.length;
  const prefilledCount = answeredCount - newCount;

  const saveDraft = () => {
    if (!respAppId) return;
    const drafts = readLocal(LS_DRAFTS, {}) || {};
    drafts[respAppId] = { answers, at: new Date().toISOString() };
    writeLocal(LS_DRAFTS, drafts);
    setMsg('Draft saved on this device. You can come back and finish later.');
  };

  const submitAnswers = async () => {
    if (!respondApp) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const fresh = (await store.getFactSheet(respAppId)) || respondApp;
      // Only what changed. Writing back an untouched prefilled value produces
      // an audit entry claiming an edit that never happened.
      const patch = {};
      newFields.forEach((f) => { patch[f.key] = answers[f.key]; });
      await store.saveFactSheet({ ...fresh, fields: { ...(fresh.fields || {}), ...patch } });

      const drafts = readLocal(LS_DRAFTS, {}) || {};
      delete drafts[respAppId];
      writeLocal(LS_DRAFTS, drafts);

      if (respondCampaign) {
        const record = {
          campaignId: respondCampaign.id,
          email: respondent,
          name: respondent,
          appId: respAppId,
          appName: respondApp.name,
          sections: respSections,
          answered: newCount,          // what this respondent supplied, not what was prefilled
          prefilled: prefilledCount,   // already on file and left as-is
          at: new Date().toISOString(),
        };
        const next = campaigns.map((c) => (c.id === respondCampaign.id
          ? { ...c, responses: (c.responses || []).filter((r) => !(r.appId === respAppId && r.email === respondent)).concat([record]) }
          : c));
        setCampaigns(next);
        if (localMode) saveLocalCampaigns(next);
        else await api('/surveys/responses', { method: 'POST', body: JSON.stringify(record) }).catch(() => saveLocalCampaigns(next));
      }
      setMsg(`Thank you — ${newCount} answer(s) saved to ${respondApp.name}`
        + (prefilledCount ? `. ${prefilledCount} field(s) already held the same value and were left alone.` : '.'));
      await load();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const setAnswer = (key, value) => setAnswers((a) => ({ ...a, [key]: value }));

  // ---- Outstanding tab -----------------------------------------------------
  const outstanding = useMemo(() => {
    const groups = new Map();
    apps.forEach((app) => {
      const missing = MANDATORY_FIELDS.filter((f) => isEmpty(app.fields ? app.fields[f.key] : undefined));
      if (!missing.length) return;
      const rawOwner = OWNER_FIELDS.map((o) => (app.fields || {})[o.key]).find((v) => String(v || '').trim());
      const ownerName = String(rawOwner || '').trim() || 'Unassigned — no owner on the fact sheet';
      const k = ownerName.toLowerCase();
      const g = groups.get(k) || {
        key: k,
        name: isEmail(ownerName) ? ownerName.split('@')[0] : ownerName,
        email: isEmail(ownerName) ? ownerName : '',
        apps: [],
        missingCount: 0,
      };
      g.apps.push({ id: app.id, name: app.name, completion: app.completion || 0, missing: missing.map((f) => f.label) });
      g.missingCount += missing.length;
      groups.set(k, g);
    });
    const rows = Array.from(groups.values());
    rows.sort((a, b) => {
      const dir = sortDir === 'asc' ? 1 : -1;
      if (sortBy === 'name') return dir * a.name.localeCompare(b.name);
      if (sortBy === 'apps') return dir * (a.apps.length - b.apps.length);
      return dir * (a.missingCount - b.missingCount);
    });
    return rows;
  }, [apps, sortBy, sortDir]);

  const totalMissing = outstanding.reduce((n, o) => n + o.missingCount, 0);

  const sortHead = (key, label, align) => (
    <TableCell align={align}>
      <TableSortLabel
        active={sortBy === key}
        direction={sortBy === key ? sortDir : 'desc'}
        onClick={() => {
          if (sortBy === key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
          else { setSortBy(key); setSortDir(key === 'name' ? 'asc' : 'desc'); }
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  // ---- Friendly field renderer (Respond tab) -------------------------------
  const renderQuestion = (f) => {
    const v = answers[f.key];
    const common = {
      fullWidth: true,
      size: 'small',
      value: v === undefined || v === null ? '' : v,
      onChange: (e) => setAnswer(f.key, e.target.value),
    };
    switch (f.type) {
      case FIELD_TYPES.TEXTAREA:
        return <TextField {...common} multiline minRows={3} placeholder="Type your answer…" />;
      case FIELD_TYPES.SELECT:
      case FIELD_TYPES.RATING:
      case FIELD_TYPES.ARROWS:
        return (
          <TextField {...common} select>
            <MenuItem value=""><em>Not sure / leave blank</em></MenuItem>
            {resolvePicklist(f.picklist).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        );
      case FIELD_TYPES.MULTISELECT:
        return (
          <TextField
            fullWidth size="small" select
            SelectProps={{ multiple: true }}
            value={Array.isArray(v) ? v : []}
            onChange={(e) => setAnswer(f.key, e.target.value)}
          >
            {resolvePicklist(f.picklist).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
          </TextField>
        );
      case FIELD_TYPES.BOOLEAN:
        return (
          <ToggleButtonGroup
            exclusive size="small"
            value={v === true ? 'yes' : v === false ? 'no' : null}
            onChange={(e, next) => setAnswer(f.key, next === 'yes' ? true : next === 'no' ? false : '')}
          >
            <ToggleButton value="yes" sx={{ px: 3 }}>Yes</ToggleButton>
            <ToggleButton value="no" sx={{ px: 3 }}>No</ToggleButton>
          </ToggleButtonGroup>
        );
      case FIELD_TYPES.NUMBER:
      case FIELD_TYPES.CURRENCY:
      case FIELD_TYPES.PERCENT:
        return <TextField {...common} type="number" onChange={(e) => setAnswer(f.key, e.target.value === '' ? '' : Number(e.target.value))} />;
      case FIELD_TYPES.DATE:
        return <TextField {...common} type="date" InputLabelProps={{ shrink: true }} />;
      default:
        return <TextField {...common} placeholder="Type your answer…" />;
    }
  };

  // ---- Render --------------------------------------------------------------
  // Computed once per campaign list rather than inside the row map, where it
  // re-ran on every unrelated state change.
  const statsByCampaign = useMemo(() => {
    const out = {};
    campaigns.forEach((c) => { out[c.id] = statsFor(c); });
    return out;
  }, [campaigns, statsFor]);

  const respondPool = (respondCampaign && respondCampaign.appIds.length
    ? respondCampaign.appIds.map((id) => appById[id]).filter(Boolean)
    : apps);
  const respondOptions = respondPool.slice(0, PICKER_CAP);
  const respondOptionsTruncated = respondPool.length > PICKER_CAP;

  const dialogFields = dialog ? fieldsForSections(dialog.sections) : [];
  // Keyed on the id LIST, not on `dialog`: setDraft returns a new dialog object
  // for every keystroke in the campaign name, which invalidated this memo each
  // time and re-ran an O(n*k) `includes` scan — ~16M comparisons after "Add
  // matching apps". Also a Set, so the scan is O(n) rather than O(n*k).
  const dialogAppIdKey = dialog ? (dialog.appIds || []).join(',') : '';
  const selectedApps = useMemo(
    () => {
      if (!dialog) return [];
      const want = new Set(dialog.appIds || []);
      return apps.filter((a) => want.has(a.id));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [dialogAppIdKey, apps]
  );

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: NAVY }}>Surveys</Typography>
        <Chip size="small" label={`${apps.length} applications`} sx={{ height: 20 }} />
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={load} title="Refresh"><RefreshIcon /></IconButton>
        <Button
          startIcon={<AddIcon />} variant="contained" onClick={openDialog}
          sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
        >
          New campaign
        </Button>
      </Stack>
      <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
        Ask owners only what is missing, only in their area — and land the answers straight on the fact sheet.
      </Typography>

      {localMode && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Primordia does not email anyone. The <code>/surveys</code> API is not deployed, so campaigns,
          recipients and reminders are recorded in this browser only — you still contact the owners
          yourself and enter their answers here. Everything else works: the response form writes straight
          to the fact sheets, and the outstanding list is live against tenant data.
        </Alert>
      )}
      {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr('')}>{err}</Alert>}
      {msg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setMsg('')}>{msg}</Alert>}

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #E0E6ED' }}>
        <Tab label="Campaigns" sx={{ fontWeight: 600 }} />
        <Tab label="Respond (preview)" sx={{ fontWeight: 600 }} />
        <Tab label={`Outstanding${totalMissing ? ` (${totalMissing})` : ''}`} sx={{ fontWeight: 600 }} />
      </Tabs>

      {loading && <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={26} /></Box>}

      {/* ------------------------------ TAB 1 ------------------------------ */}
      {!loading && tab === 0 && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow sx={headSx}>
                <TableCell>Campaign</TableCell>
                <TableCell align="right">Apps</TableCell>
                <TableCell align="right">Questions</TableCell>
                <TableCell align="right">Recipients</TableCell>
                <TableCell>{localMode ? 'Created' : 'Sent'}</TableCell>
                <TableCell>Due</TableCell>
                <TableCell align="right">Responses</TableCell>
                <TableCell>Complete</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {campaigns.map((c) => {
                const s = statsByCampaign[c.id];
                return (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: NAVY }}>{c.name}</Typography>
                      <SectionChips sections={c.sections || []} />
                    </TableCell>
                    <TableCell align="right">{(c.appIds || []).length}</TableCell>
                    <TableCell align="right">{s.questions}</TableCell>
                    <TableCell align="right">{s.recipients}</TableCell>
                    <TableCell>{shortDate(c.sentAt)}</TableCell>
                    <TableCell>{c.dueDate || '—'}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2">{s.received} in</Typography>
                      <Typography variant="caption" sx={{ color: s.outstanding ? '#B26A00' : '#2E7D32' }}>
                        {s.outstanding} outstanding
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Progress value={s.responsePct} />
                      <Tooltip title="Share of the scoped questions that now have an answer on the fact sheets">
                        <Typography variant="caption" sx={{ color: GREY }}>
                          {s.dataPct}% of the {s.questions} fields this campaign asks about are captured
                        </Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Tooltip
                        title={localMode
                          ? 'List everyone still outstanding and log the chase in this browser. No email is sent.'
                          : 'Send reminders to everyone outstanding'}
                      >
                        <span>
                          <IconButton size="small" onClick={() => sendReminders(c)} disabled={!s.outstanding}>
                            <NotificationsActiveIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Preview what the recipient sees">
                        <IconButton
                          size="small"
                          onClick={() => { setRespCampaignId(c.id); setTab(1); }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {!campaigns.length && (
                <TableRow>
                  <TableCell colSpan={9} sx={{ color: '#9AA6B2', textAlign: 'center', py: 4 }}>
                    No campaigns yet. Create one to stop chasing owners by hand.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}

      {/* ------------------------------ TAB 2 ------------------------------ */}
      {!loading && tab === 1 && (
        <Box>
          <Paper sx={{ p: 2, mb: 2 }}>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                select size="small" label="Campaign (optional)" sx={{ minWidth: 220 }}
                value={respCampaignId} onChange={(e) => setRespCampaignId(e.target.value)}
              >
                <MenuItem value=""><em>None — ad-hoc preview</em></MenuItem>
                {campaigns.map((c) => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
              </TextField>
              <TextField
                select size="small" label="Application" sx={{ minWidth: 240 }}
                value={respAppId} onChange={(e) => setRespAppId(e.target.value)}
              >
                <MenuItem value=""><em>Choose an application</em></MenuItem>
                {/* Capped like AiEnrichment's ROW_CAP and SavedViews' slice(0,300):
                    a MUI Select mounts every MenuItem, and a real tenant has
                    thousands. A campaign's own list is already bounded. */}
                {respondOptions.map((a) => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}
                {respondOptionsTruncated && (
                  <MenuItem disabled>
                    <em>
                      Showing the first {PICKER_CAP.toLocaleString()} of {apps.length.toLocaleString()} —
                      choose a campaign to narrow the list
                    </em>
                  </MenuItem>
                )}
              </TextField>
              <Autocomplete
                multiple size="small" options={SECTION_NAMES} value={respSections}
                onChange={(e, v) => setRespSections(v)} sx={{ flex: 1, minWidth: 260 }}
                renderInput={(params) => <TextField {...params} label="Sections in scope" />}
              />
              <TextField
                size="small" label="Responding as" sx={{ minWidth: 200 }}
                value={respondent} onChange={(e) => setRespondent(e.target.value)}
              />
            </Stack>
          </Paper>

          {!respondApp || !respSections.length ? (
            <Paper sx={{ p: 4, textAlign: 'center', color: '#9AA6B2' }}>
              Pick an application and at least one section to see the owner's questionnaire.
            </Paper>
          ) : (
            <Paper sx={{ p: 0, maxWidth: 900 }}>
              <Box sx={{ p: 3, bgcolor: NAVY, color: '#fff' }}>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {respondCampaign ? respondCampaign.name : 'Application discovery'}
                </Typography>
                <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
                  About <b>{respondApp.name}</b>
                  {respondCampaign && respondCampaign.dueDate ? ` — please reply by ${respondCampaign.dueDate}` : ''}
                </Typography>
              </Box>
              <Box sx={{ px: 3, py: 2, bgcolor: '#F5F8FB' }}>
                <Typography variant="body2" sx={{ color: GREY, mb: 1.5 }}>
                  {respondCampaign ? respondCampaign.message : DEFAULT_MESSAGE}
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Progress value={pct(answeredCount, respondFields.length)} width={200} />
                  <Typography variant="caption" sx={{ color: GREY }}>
                    {answeredCount} of {respondFields.length} populated
                    {prefilledCount > 0 && ` · ${newCount} new, ${prefilledCount} already on file`}
                  </Typography>
                </Stack>
              </Box>
              <Divider />
              <Box sx={{ p: 3 }}>
                {respSections.map((section) => {
                  const fields = respondFields.filter((f) => f.section === section);
                  if (!fields.length) return null;
                  return (
                    <Box key={section} sx={{ mb: 4 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: HEAD, mb: 2 }}>
                        {section}
                      </Typography>
                      <Stack spacing={3}>
                        {fields.map((f) => (
                          <Box key={f.key}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2" sx={{ fontWeight: 600, color: NAVY }}>
                                {f.label}{f.mandatory ? ' *' : ''}
                              </Typography>
                              {!isEmpty(answers[f.key]) && <CheckCircleIcon sx={{ fontSize: 15, color: '#2E7D32' }} />}
                            </Stack>
                            <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1 }}>
                              {helperFor(f)}
                            </Typography>
                            {renderQuestion(f)}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
              <Divider />
              <Stack direction="row" spacing={2} sx={{ p: 2 }} justifyContent="flex-end">
                <Button startIcon={<SaveIcon />} onClick={saveDraft} disabled={busy}>Save draft</Button>
                <Button
                  variant="contained" startIcon={<SendIcon />} onClick={submitAnswers} disabled={busy}
                  sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
                >
                  {busy ? 'Submitting…' : 'Submit answers'}
                </Button>
              </Stack>
            </Paper>
          )}
        </Box>
      )}

      {/* ------------------------------ TAB 3 ------------------------------ */}
      {!loading && tab === 2 && (
        <Box>
          <Typography variant="body2" sx={{ color: GREY, mb: 2 }}>
            {totalMissing} mandatory answer(s) still outstanding across {outstanding.reduce((n, o) => n + o.apps.length, 0)} application(s),
            grouped by the person who owns them.
          </Typography>
          <Paper sx={{ mb: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={headSx}>
                  {sortHead('name', 'Owner')}
                  {sortHead('apps', 'Applications', 'right')}
                  {sortHead('missing', 'Missing answers', 'right')}
                  <TableCell align="right">Chase</TableCell>
                </TableRow>
              </TableHead>
            </Table>
          </Paper>

          {outstanding.map((o) => (
            <Accordion key={o.key} disableGutters sx={{ mb: 0.5 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: NAVY, minWidth: 220 }}>{o.name}</Typography>
                  <Chip
                    size="small" sx={{ height: 20 }}
                    label={o.email || 'no email on file'}
                    color={o.email ? 'default' : 'warning'}
                    variant="outlined"
                  />
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="caption" sx={{ color: GREY }}>{o.apps.length} app(s)</Typography>
                  <Chip size="small" label={`${o.missingCount} missing`} sx={{ height: 20, bgcolor: '#FDECEA', color: '#B3261E' }} />
                  <Tooltip
                    title={localMode
                      ? 'Show what this owner still owes so you can chase them yourself. No email is sent.'
                      : 'Email this owner a reminder'}
                  >
                    <Button
                      size="small" startIcon={<NotificationsActiveIcon />}
                      onClick={(e) => { e.stopPropagation(); remindOwner(o); }}
                      sx={{ color: BLUE }}
                    >
                      {localMode ? 'Chase list' : 'Remind'}
                    </Button>
                  </Tooltip>
                </Stack>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: '#F9FBFD' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={headSx}>
                      <TableCell>Application</TableCell>
                      <TableCell>Completion</TableCell>
                      <TableCell>Still needed</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {o.apps
                      .slice()
                      .sort((a, b) => b.missing.length - a.missing.length)
                      .map((a) => (
                        <TableRow key={a.id} hover>
                          <TableCell sx={{ fontWeight: 600 }}>{a.name}</TableCell>
                          <TableCell sx={{ width: 190 }}><Progress value={a.completion} width={110} /></TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                              {a.missing.map((m) => (
                                <Chip key={m} size="small" label={m} variant="outlined" sx={{ height: 20 }} />
                              ))}
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </AccordionDetails>
            </Accordion>
          ))}

          {!outstanding.length && (
            <Paper sx={{ p: 4, textAlign: 'center', color: '#9AA6B2' }}>
              Nothing outstanding — every application has its mandatory fields.
            </Paper>
          )}
        </Box>
      )}

      {/* --------------------------- CREATE DIALOG -------------------------- */}
      <Dialog open={!!dialog} onClose={() => !busy && setDialog(null)} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>New survey campaign</DialogTitle>
        {dialog && (
          <DialogContent dividers>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 3 }}>
              <TextField
                fullWidth size="small" label="Campaign name" value={dialog.name}
                onChange={(e) => setDraft({ name: e.target.value })}
                placeholder="e.g. Wave 1 — DR & business continuity"
              />
              <TextField
                size="small" type="date" label="Due date" InputLabelProps={{ shrink: true }}
                sx={{ minWidth: 180 }} value={dialog.dueDate}
                onChange={(e) => setDraft({ dueDate: e.target.value })}
              />
            </Stack>

            {/* 1. Applications */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: HEAD, mb: 1 }}>
              1. Which applications? ({dialog.appIds.length} selected)
            </Typography>
            <Autocomplete
              multiple size="small" options={apps} value={selectedApps}
              // MUI filters the full list on each keystroke but only renders the
              // matches; the cap keeps the initial open bounded on a large tenant.
              filterOptions={(opts, state) => {
                const q = String(state.inputValue || '').toLowerCase();
                const hits = q ? opts.filter((o) => String(o.name || '').toLowerCase().includes(q)) : opts;
                return hits.slice(0, PICKER_CAP);
              }}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              onChange={(e, v) => setDraft({ appIds: v.map((a) => a.id) })}
              renderInput={(params) => <TextField {...params} label="Search applications" placeholder="Type to search…" />}
              sx={{ mb: 1 }}
            />
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 3 }}>
              <TextField
                select size="small" label="…or all apps missing" sx={{ minWidth: 280 }}
                value={missingFieldKey} onChange={(e) => setMissingFieldKey(e.target.value)}
              >
                {UNIQUE_FIELDS.slice().sort((a, b) => a.label.localeCompare(b.label)).map((f) => (
                  <MenuItem key={f.key} value={f.key}>{f.label}</MenuItem>
                ))}
              </TextField>
              <Button size="small" onClick={addAppsMissingField} disabled={!missingFieldKey} sx={{ color: BLUE }}>
                Add matching apps
              </Button>
              {dialog.appIds.length > 0 && (
                <Button size="small" onClick={() => setDraft({ appIds: [], recipients: [] })} sx={{ color: GREY }}>
                  Clear
                </Button>
              )}
            </Stack>

            {/* 2. Sections */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: HEAD, mb: 0.5 }}>
              2. Which sections should we ask about?
            </Typography>
            <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1 }}>
              Scoping is what keeps a survey short enough to get answered — {dialogFields.length} question(s) so far.
            </Typography>
            <Box sx={{ mb: 3, maxHeight: 190, overflowY: 'auto', border: '1px solid #E0E6ED', borderRadius: 1, p: 1 }}>
              {SECTION_NAMES.map((s) => (
                <FormControlLabel
                  key={s}
                  sx={{ display: 'block', ml: 0 }}
                  control={
                    <Checkbox
                      size="small"
                      checked={dialog.sections.includes(s)}
                      onChange={(e) => setDraft({
                        sections: e.target.checked
                          ? dialog.sections.concat([s])
                          : dialog.sections.filter((x) => x !== s),
                      })}
                    />
                  }
                  label={<span style={{ fontSize: 13 }}>{s} <span style={{ color: GREY }}>({SECTION_SIZE[s]} questions)</span></span>}
                />
              ))}
            </Box>

            {/* 3. Recipients */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: HEAD, mb: 0.5 }}>
              3. Who do we ask? ({dialog.recipients.length} selected)
            </Typography>
            <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1 }}>
              Suggested from the owner fields on the selected applications.
            </Typography>
            <Box sx={{ mb: 1, maxHeight: 220, overflowY: 'auto', border: '1px solid #E0E6ED', borderRadius: 1, p: 1 }}>
              {!suggestedRecipients.length && (
                <Typography variant="caption" sx={{ color: '#9AA6B2' }}>
                  {dialog.appIds.length
                    ? 'No owners recorded on these applications — add recipients manually below.'
                    : 'Select applications to see suggested owners.'}
                </Typography>
              )}
              {suggestedRecipients.map((s) => {
                const selected = dialog.recipients.some((r) => r.key === s.key);
                const email = emailEdits[s.key] !== undefined ? emailEdits[s.key] : s.email;
                return (
                  <Stack key={s.key} direction="row" spacing={1} alignItems="center" sx={{ py: 0.5 }}>
                    <Checkbox size="small" checked={selected} onChange={() => toggleRecipient(s)} />
                    <Box sx={{ minWidth: 200 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>{s.name}</Typography>
                      <Typography variant="caption" sx={{ color: GREY }}>
                        {s.roles.join(', ')} — {s.appIds.length} app(s)
                      </Typography>
                    </Box>
                    <TextField
                      size="small" placeholder="email address" sx={{ flex: 1 }}
                      value={email} onChange={(e) => setRecipientEmail(s.key, e.target.value)}
                      error={!!email && !isEmail(email)}
                    />
                  </Stack>
                );
              })}
            </Box>
            <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
              <TextField
                size="small" fullWidth label="Add a recipient by email"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { addManualRecipient(e.target.value); e.target.value = ''; e.preventDefault(); }
                }}
                helperText="Press Enter to add."
              />
            </Stack>
            {!!dialog.recipients.length && (
              <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
                {dialog.recipients.map((r) => (
                  <Chip
                    key={r.key} size="small" label={r.email || `${r.name} (no email)`}
                    onDelete={() => setDraft({ recipients: dialog.recipients.filter((x) => x.key !== r.key) })}
                    color={r.email ? 'default' : 'warning'}
                  />
                ))}
              </Stack>
            )}

            {/* 4. Message */}
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: HEAD, mb: 0.5 }}>4. Message</Typography>
            {localMode && (
              <Typography variant="caption" sx={{ color: GREY, display: 'block', mb: 1 }}>
                Primordia does not send this. Copy it into your own mail client when you contact the
                recipients — it is saved with the campaign so you can reuse it.
              </Typography>
            )}
            <TextField
              fullWidth size="small" multiline minRows={3} value={dialog.message}
              onChange={(e) => setDraft({ message: e.target.value })}
            />
          </DialogContent>
        )}
        <DialogActions>
          <Button onClick={() => setDialog(null)} disabled={busy}>Cancel</Button>
          <Button
            variant="contained" startIcon={localMode ? <SaveIcon /> : <SendIcon />} onClick={createCampaign}
            disabled={busy || !dialog || !dialog.appIds.length || !dialog.sections.length || !dialog.recipients.length}
            sx={{ bgcolor: BLUE, '&:hover': { bgcolor: NAVY } }}
          >
            {busy && 'Saving…'}
            {!busy && (localMode ? 'Create campaign' : 'Create & send')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
